import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Clock, Loader2, Copy } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];
const DEFAULT = Array.from({ length: 7 }, () => ({ open: '09:00', close: '00:00', closed: false }));

const normalize = (wh) => DEFAULT.map((d, i) => ({
  open: String(wh?.[i]?.open || d.open).slice(0, 5),
  close: String(wh?.[i]?.close || d.close).slice(0, 5),
  closed: !!wh?.[i]?.closed,
}));

const label = (h) => (h.closed ? 'Kapalı' : `${h.open} – ${h.close === '00:00' ? '24:00' : h.close}`);

const SalonHoursModal = ({ open, onOpenChange, salon, onSaved }) => {
  const [hours, setHours] = useState(DEFAULT);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && salon) setHours(normalize(salon.working_hours));
  }, [open, salon]);

  const setDay = (i, patch) => setHours((h) => h.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));
  const applyToAll = () => {
    const first = hours[0];
    setHours(hours.map(() => ({ ...first })));
    toast.success('İlk günün saatleri tüm günlere uygulandı');
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/salons/${salon.id}/working-hours`, { working_hours: hours });
      toast.success('Çalışma saatleri kaydedildi');
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Çalışma saatleri kaydedilemedi');
    } finally { setSaving(false); }
  };

  if (!salon) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="salon-hours-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#176B91]" /> Çalışma Saatleri — {salon.name}
          </DialogTitle>
          <DialogDescription>Her gün için açılış ve kapanış saatini ayrı ayrı belirleyin (00:00 = gece yarısı)</DialogDescription>
        </DialogHeader>

        <div className="flex justify-end">
          <Button type="button" variant="outline" size="sm" className="h-8 text-[11px]"
            onClick={applyToAll} data-testid="salon-hours-apply-all">
            <Copy className="w-3.5 h-3.5 mr-1" /> Pazartesi'yi tüm günlere uygula
          </Button>
        </div>

        <div className="space-y-2">
          {hours.map((h, i) => (
            <div key={DAYS[i]} className="flex items-center gap-2 rounded-lg border border-[#E6EDF3] px-3 py-2"
              data-testid={`salon-hours-row-${i}`}>
              <span className="w-24 text-[12px] font-semibold text-slate-700">{DAYS[i]}</span>
              <Input type="time" className="h-8 w-28" value={h.open} disabled={h.closed}
                onChange={(e) => setDay(i, { open: e.target.value })} data-testid={`salon-hours-open-${i}`} />
              <span className="text-slate-400">–</span>
              <Input type="time" className="h-8 w-28" value={h.close} disabled={h.closed}
                onChange={(e) => setDay(i, { close: e.target.value })} data-testid={`salon-hours-close-${i}`} />
              <label className="flex items-center gap-1.5 ml-auto cursor-pointer">
                <input type="checkbox" className="h-3.5 w-3.5 rounded border-gray-300" checked={h.closed}
                  onChange={(e) => setDay(i, { closed: e.target.checked })} data-testid={`salon-hours-closed-${i}`} />
                <span className="text-[11px] text-slate-500">Kapalı</span>
              </label>
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Vazgeç</Button>
          <Button type="button" onClick={save} disabled={saving} className="bg-[#176B91] hover:bg-[#12566F]"
            data-testid="salon-hours-save">
            {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Kaydet
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { normalize as normalizeWorkingHours, label as workingHoursLabel };
export default SalonHoursModal;
